"use client"

import { Menu, X } from "lucide-react"
import Link from "next/link"
import { usePathname } from "next/navigation"

import AnimatedLogo from "@/core/ui/animated-logo"
import { Button } from "@/core/ui/button"
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle } from "@/core/ui/sheet"
import { cn } from "@/core/utils/utils"

const navItems = [
    { name: "Work", href: "/work" },
    { name: "Writing", href: "/writing" },
    { name: "Contact", href: "/contact" },
]

/**
 * Mobile navigation sheet component.
 * Upgraded to "Million Dollar" Premium Dark typography and animations.
 */
export function MobileNav() {
    const pathname = usePathname()

    return (
        <Sheet>
            <SheetTrigger asChild>
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10 md:hidden rounded-full hover:bg-white/5 border border-transparent hover:border-white/10 text-white"
                    aria-label="Open navigation menu"
                >
                    <Menu className="h-5 w-5" aria-hidden="true" />
                    <span className="sr-only">Open navigation menu</span>
                </Button>
            </SheetTrigger>
            <SheetContent
                side="right"
                className="w-full sm:w-[400px] border-l border-white/5 bg-black/60 backdrop-blur-3xl p-0"
            >
                {/* Subtle noise overlay for premium physical texture */}
                <div className="absolute inset-0 z-0 opacity-[0.1] mix-blend-overlay pointer-events-none" style={{ backgroundImage: 'url("/noise.svg")' }} />
                
                <SheetTitle className="sr-only">Navigation Menu</SheetTitle>

                <div className="relative z-10 flex flex-col h-full p-6">
                    {/* Mobile header */}
                    <div className="flex items-center justify-between pb-6 border-b border-white/5">
                        <AnimatedLogo className="h-8 pl-1" />
                        <SheetClose className="p-2 rounded-full hover:bg-white/10 transition-colors text-white/50 hover:text-white">
                            <X className="w-5 h-5" />
                            <span className="sr-only">Close</span>
                        </SheetClose>
                    </div>

                    {/* High-End Typographic Mobile nav */}
                    <nav className="flex flex-col gap-8 py-14 group/mobilenav">
                        {navItems.map((item, index) => {
                            const isActive = pathname === item.href || pathname.startsWith(item.href)
                            return (
                                <SheetClose asChild key={item.href}>
                                    <Link
                                        href={item.href}
                                        className={cn(
                                            "relative flex items-center text-4xl sm:text-5xl font-bold tracking-tighter transition-all duration-500",
                                            "hover:translate-x-4",
                                            "text-white/40 hover:!text-white group-hover/mobilenav:text-white/20",
                                            isActive ? "text-white translate-x-4" : ""
                                        )}
                                        style={{ animation: `slide-fade-in 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards ${Math.max(0.1, index * 0.1)}s`, opacity: 0 }}
                                    >
                                        {/* Active line indicator */}
                                        <span className={cn(
                                            "absolute -left-6 w-3 h-[2px] bg-white transition-all duration-500",
                                            isActive ? "opacity-100 scale-x-100" : "opacity-0 scale-x-0"
                                        )} />
                                        {item.name}
                                    </Link>
                                </SheetClose>
                            )
                        })}
                    </nav>

                    {/* Premium Dark Mobile CTA */}
                    <div className="mt-auto pt-8 pb-4">
                        <SheetClose asChild>
                            <Link
                                href="/contact"
                                className="relative group overflow-hidden rounded-full p-[1px] flex"
                            >
                                <span className="absolute inset-0 bg-gradient-to-b from-white/30 to-transparent rounded-full opacity-50" />
                                <div className="relative flex items-center justify-center gap-2 w-full px-5 py-4 rounded-full text-lg font-medium bg-black text-white hover:bg-neutral-900 transition-all shadow-[inset_0_1px_1px_rgba(255,255,255,0.15)] group-hover:shadow-[0_0_20px_rgba(255,255,255,0.1)]">
                                    <span className="relative z-10 transition-transform group-hover:translate-x-0.5">Start a Project</span>
                                    <span className="relative z-10 transition-transform group-hover:translate-x-1">→</span>
                                </div>
                            </Link>
                        </SheetClose>
                    </div>
                </div>
                
                {/* CSS animation for stagger fade */}
                <style>{`
                    @keyframes slide-fade-in {
                        from { opacity: 0; transform: translateX(-30px); }
                        to { opacity: 1; transform: translateX(0); }
                    }
                `}</style>
            </SheetContent>
        </Sheet>
    )
}
